/* eslint-disable @typescript-eslint/no-explicit-any */
import { v4 as uuidv4 } from 'uuid';
import { BaseResponse } from '../Contracts/responses';
import { SettingsLogger } from '../utils/SettingsLogger';

class MessageToNative {
	readonly id: string;
	readonly resolve: Function;
	readonly reject: Function;

	constructor(resolve: Function, reject?: Function) {
		this.id = uuidv4();
		this.resolve = resolve;
		this.reject = reject;
	}
}

export interface MessageFromNative<T extends BaseResponse> {
	type: 'call' | 'callback';
	id: string;
	name: string;
	params: T;
}

export class NativeBridgeMapper {
	private requestMap: Map<string, MessageToNative> = new Map();
	private functionMap: { [key: string]: Function };

	public setFunctionMap(functionMap: { [key: string]: Function }): void {
		this.functionMap = functionMap;
	}

	protected addRequestToQueue(resolve: Function, reject?: Function): string {
		const messageToNative = new MessageToNative(resolve, reject);
		this.requestMap.set(messageToNative.id, messageToNative);
		return messageToNative.id;
	}

	public onMessageReceived<T extends BaseResponse>(message: MessageFromNative<T>): void {
		switch (message.type) {
			case 'call': {
				const { id, name, params } = message;
				if (this.functionMap[name]) {
					this.functionMap[name].call(this, { id, params });
				} else {
					console.warn(`Function ${name} not found in function map`);
				}
				break;
			}
			case 'callback': {
				const { id, params } = message;
				const messageFromNative = this.requestMap.get(id);
				if (messageFromNative) {
					this.requestMap.delete(id);
					if (params.status === 0) {
						// Success - resolve with data field for flat response
						messageFromNative.resolve(params);
					} else {
						// Failure - extract error details from flat response
						const errorDetails = (params as any).error || {
							errorDescription: 'Settings operation failed',
							errorCode: 'UNKNOWN_ERROR',
						};
						const error = new Error(
							errorDetails.errorDescription || 'Settings operation failed'
						);
						(error as any).errorCode = errorDetails.errorCode;
						SettingsLogger.getInstance().write(
							'error',
							`Settings native call failed: ${
								errorDetails.errorDescription || 'Unknown error'
							}`
						);
						if (messageFromNative.reject) {
							messageFromNative.reject(error);
						} else {
							messageFromNative.resolve(error);
						}
					}
				}
				break;
			}
		}
	}
}
